/**
 * Permalink codec.
 *
 * Wire format, version 1:
 *
 *   byte 0        version (1)
 *   byte 1        photo ref kind: 0 = catalog id, 1 = upload id
 *   byte 2        L, length of the id in bytes (ASCII, 0..32)
 *   L bytes       id
 *   4 bytes       seed, little-endian u32
 *   byte          flags
 *   byte          P, number of quantised parameters that follow
 *   P bytes       parameters in PARAM_TABLE order
 *
 * Encoded as base64url without padding. Decoding accepts more parameters than
 * it knows (ignored) and fewer (defaults), so links survive schema growth.
 */

import { PARAM_TABLE, defaultParams, dequantise, quantise, type SceneParams } from './params';

export const CODEC_VERSION = 1;
const MAX_ID_LENGTH = 32;

export const enum PhotoKind {
  Catalog = 0,
  Upload = 1,
}

const UPLOAD_PREFIX = 'u:';

export function encodeScene(p: SceneParams): string {
  const id = p.photo.startsWith(UPLOAD_PREFIX) ? p.photo.slice(UPLOAD_PREFIX.length) : p.photo;
  const kind = p.photo.startsWith(UPLOAD_PREFIX) ? PhotoKind.Upload : PhotoKind.Catalog;
  if (!/^[A-Za-z0-9_-]{1,32}$/.test(id)) {
    throw new Error(`photo id not encodable: ${p.photo}`);
  }
  const bytes = new Uint8Array(3 + id.length + 4 + 1 + 1 + PARAM_TABLE.length);
  let o = 0;
  bytes[o++] = CODEC_VERSION;
  bytes[o++] = kind;
  bytes[o++] = id.length;
  for (let i = 0; i < id.length; i++) bytes[o++] = id.charCodeAt(i);
  const seed = p.seed >>> 0;
  bytes[o++] = seed & 0xff;
  bytes[o++] = (seed >>> 8) & 0xff;
  bytes[o++] = (seed >>> 16) & 0xff;
  bytes[o++] = (seed >>> 24) & 0xff;
  bytes[o++] = p.flags & 0xff;
  bytes[o++] = PARAM_TABLE.length;
  for (const spec of PARAM_TABLE) bytes[o++] = quantise(spec, p[spec.key]);
  return toBase64Url(bytes);
}

export function decodeScene(code: string): SceneParams {
  const bytes = fromBase64Url(code);
  const p = defaultParams();
  if (bytes.length < 3) throw new Error('scene code too short');
  let o = 0;
  const version = bytes[o++]!;
  if (version !== CODEC_VERSION) throw new Error(`unknown scene version ${version}`);
  const kind = bytes[o++]!;
  const len = bytes[o++]!;
  if (len > MAX_ID_LENGTH || o + len > bytes.length) throw new Error('bad photo id length');
  let id = '';
  for (let i = 0; i < len; i++) id += String.fromCharCode(bytes[o++]!);
  if (!/^[A-Za-z0-9_-]*$/.test(id)) throw new Error('bad photo id');
  p.photo = kind === PhotoKind.Upload ? UPLOAD_PREFIX + id : id;
  if (o + 4 > bytes.length) throw new Error('missing seed');
  p.seed = (bytes[o]! | (bytes[o + 1]! << 8) | (bytes[o + 2]! << 16) | (bytes[o + 3]! << 24)) >>> 0;
  o += 4;
  if (o < bytes.length) p.flags = bytes[o++]!;
  if (o < bytes.length) {
    const count = bytes[o++]!;
    for (let i = 0; i < count && o < bytes.length; i++, o++) {
      const spec = PARAM_TABLE[i];
      if (spec) p[spec.key] = dequantise(spec, bytes[o]!);
    }
  }
  return p;
}

export function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Where the scene code lives in the URL. During milestone 1 the page is
 * static and uses the fragment (`/#s=CODE`). Milestone 3 adds `/s/CODE` for
 * link previews; both forms are read forever.
 */
export function sceneCodeFromLocation(loc: { pathname: string; hash: string }): string | null {
  const path = loc.pathname.match(/^\/s\/([A-Za-z0-9_-]+)\/?$/);
  if (path) return path[1]!;
  const hash = loc.hash.match(/^#s=([A-Za-z0-9_-]+)$/);
  if (hash) return hash[1]!;
  return null;
}

export function sceneUrl(origin: string, code: string, style: 'hash' | 'path' = 'hash'): string {
  return style === 'path' ? `${origin}/s/${code}` : `${origin}/#s=${code}`;
}
